import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Bell,
  Edit3,
  Gift,
  Heart,
  ListChecks,
  MessageSquare,
  Pause,
  PauseCircle,
  Plus,
  Radio,
  RefreshCw,
  Save,
  Send,
  Sparkles,
  Star,
  Trophy,
  Users,
  Volume2,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ActionEditor, actionFromApi, prepareActionsPayload } from "./InteractionsAdmin";
import IconPicker from "../components/IconPicker";
import { CurrencyIcon } from "../lib/currencyDisplay";
import { DEFAULT_OBS_CONFIG, normalizeObsConfig, useObs } from "../hooks/useObs";

const OBS_LOCAL_CONFIG_KEY = "thebarracksbunny.obs.localConfig";

function loadLocalObsConfig() {
  try {
    return normalizeObsConfig({
      ...DEFAULT_OBS_CONFIG,
      ...JSON.parse(localStorage.getItem(OBS_LOCAL_CONFIG_KEY) || "{}"),
    });
  } catch (err) {
    return DEFAULT_OBS_CONFIG;
  }
}

function asArray(value, keys = []) {
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return [];
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

const defaultButtonDraft = {
  label: "New Button",
  icon: "Sparkles",
  accent_color: "#0A84FF",
  enabled: true,
  mode: "fire_once",
  button_type: "custom",
  current_state: false,
  actions: [],
  on_actions: [],
  off_actions: [],
};

const streamDeckFunctionOptions = [
  {
    value: "custom",
    label: "Custom action chain",
    mode: "fire_once",
    description: "Build your own ordered runtime actions.",
  },
  {
    value: "media.video.play",
    label: "Play overlay video",
    mode: "fire_once",
    description: "Shows a configured video through the overlay/media runtime.",
    actions: [
      { action_type: "show_video_media", config: { media_type: "video", media_reference: "", x: 100, y: 100, width: 640, height: 360, duration: null, chroma_key_enabled: false, chroma_key_color: "#00ff00", chroma_key_similarity: 0.35, chroma_key_smoothness: 0.08 } },
    ],
  },
  {
    value: "obs.filter.toggle_chain",
    label: "Toggle OBS filters",
    mode: "toggle",
    description: "Runs one OBS filter chain when turning on, and another when turning off.",
    on_actions: [
      { action_type: "obs_filter_enable", config: { scene_name: "", source_name: "", filter_name: "", enabled: true } },
    ],
    off_actions: [
      { action_type: "obs_filter_disable", config: { scene_name: "", source_name: "", filter_name: "", enabled: false } },
    ],
  },
  {
    value: "obs.source.toggle",
    label: "Toggle OBS source",
    mode: "fire_once",
    description: "Flips one OBS source between visible and hidden.",
    actions: [
      { action_type: "toggle_source", config: { scene_name: "", source_name: "" } },
    ],
  },
  {
    value: "twitch.clip.create",
    label: "Create Twitch clip",
    mode: "fire_once",
    description: "Runs the existing Twitch clip action path.",
    actions: [
      { action_type: "twitch_create_clip", config: { title: "", duration: 30 } },
    ],
  },
];

function streamDeskActionDraft(action) {
  return {
    id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    action_type: action.action_type,
    enabled: action.enabled !== false,
    delay_ms: action.delay_ms || 0,
    config_text: JSON.stringify(action.config || {}, null, 2),
  };
}

function applyStreamDeckFunction(draft, buttonType) {
  const option = streamDeckFunctionOptions.find((item) => item.value === buttonType) || streamDeckFunctionOptions[0];
  const next = {
    ...draft,
    button_type: option.value,
    mode: option.mode,
  };
  if (option.mode === "toggle") {
    next.actions = asArray(draft.actions);
    next.on_actions = (asArray(draft.on_actions).length ? asArray(draft.on_actions) : asArray(option.on_actions).map(streamDeskActionDraft));
    next.off_actions = (asArray(draft.off_actions).length ? asArray(draft.off_actions) : asArray(option.off_actions).map(streamDeskActionDraft));
  } else {
    next.actions = (asArray(draft.actions).length ? asArray(draft.actions) : asArray(option.actions).map(streamDeskActionDraft));
    next.on_actions = asArray(draft.on_actions);
    next.off_actions = asArray(draft.off_actions);
  }
  return next;
}

export default function Overview() {
  const { me } = useAuth();
  const [status, setStatus] = useState(null);
  const [backendReachable, setBackendReachable] = useState(null);
  const [queue, setQueue] = useState([]);
  const [events, setEvents] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [previewPulseEvents, setPreviewPulseEvents] = useState([]);
  const [hype, setHype] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [chatBadges, setChatBadges] = useState({});
  const [chatInput, setChatInput] = useState("");
  const [selectedViewer, setSelectedViewer] = useState(null);
  const [viewerNote, setViewerNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [streamDeskButtons, setStreamDeskButtons] = useState([]);
  const [streamDeskBusy, setStreamDeskBusy] = useState("");
  const [streamDeskError, setStreamDeskError] = useState("");
  const [editingButtonId, setEditingButtonId] = useState("");
  const [buttonDraft, setButtonDraft] = useState(null);
  const [obsConfig, setObsConfig] = useState(DEFAULT_OBS_CONFIG);
  const { connected: obsConnected, url: obsUrl, request: obsRequest, connect: connectObs } = useObs(obsConfig);
  const scroller = useRef(null);
  const shouldFollowChat = useRef(true);
  const [chatPaused, setChatPaused] = useState(false);

  const loadLiveData = useCallback(async () => {
    const [statusResult, queueResult, eventResult, redemptionResult, streamDeskResult, hypeResult, activityResult] = await Promise.allSettled([
      api.get("/status"),
      api.get("/interaction-requests/queue", { params: { limit: 12 } }),
      api.get("/events/recent?limit=8"),
      api.get("/redemptions?status_filter=pending&limit=8"),
      api.get("/admin/stream-desk/buttons"),
      api.get("/admin/hype"),
      api.get("/admin/platform-events", { params: { limit: 80, source: "all" } }),
    ]);

    if (statusResult.status === "fulfilled") {
      setStatus(statusResult.value.data || null);
      setBackendReachable(true);
    } else {
      setStatus(null);
      setBackendReachable(false);
    }
    if (queueResult.status === "fulfilled") setQueue(asArray(queueResult.value.data, ["requests", "queue", "items"]));
    if (eventResult.status === "fulfilled") setEvents(asArray(eventResult.value.data, ["events", "items"]));
    if (hypeResult.status === "fulfilled") setHype(hypeResult.value.data?.state || null);
    if (activityResult.status === "fulfilled") {
      setActivityLog(streamPulseEvents(asArray(activityResult.value.data, ["items", "events", "activity"])));
    }
    if (redemptionResult.status === "fulfilled") setRedemptions(asArray(redemptionResult.value.data, ["redemptions", "items"]));
    if (streamDeskResult.status === "fulfilled") {
      setStreamDeskButtons(asArray(streamDeskResult.value.data, ["buttons", "items"]));
      setStreamDeskError("");
    } else {
      setStreamDeskButtons([]);
      setStreamDeskError("Could not load Stream Desk buttons.");
    }
  }, []);

  const loadChat = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/recent?limit=120");
      setMessages(asArray(data, ["messages", "items"]));
    } catch (err) {
      // Keep the streamer panel usable even if chat polling is offline.
    }
  }, []);

  const loadChatBadges = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/badges");
      setChatBadges(asObject(data?.badges));
    } catch (err) {
      setChatBadges({});
    }
  }, []);

  useEffect(() => {
    loadLiveData();
    const id = setInterval(loadLiveData, 7000);
    return () => clearInterval(id);
  }, [loadLiveData]);

  useEffect(() => {
    setObsConfig(loadLocalObsConfig());
  }, []);

  useEffect(() => {
    loadChat();
    loadChatBadges();
    const id = setInterval(loadChat, 2500);
    return () => clearInterval(id);
  }, [loadChat, loadChatBadges]);

  useEffect(() => {
    if (!shouldFollowChat.current || !scroller.current) return;
    scroller.current.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages, activityLog]);

  const handleChatScroll = useCallback(() => {
    const node = scroller.current;
    if (!node) return;
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    const isNearBottom = distanceFromBottom < 96;
    shouldFollowChat.current = isNearBottom;
    setChatPaused(!isNearBottom);
  }, []);

  const jumpChatToLatest = useCallback(() => {
    const node = scroller.current;
    if (!node) return;
    shouldFollowChat.current = true;
    setChatPaused(false);
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
  }, []);

  const pendingCount = useMemo(
    () => asArray(queue).filter((item) => item.status === "pending" || item.status === "pending_approval").length,
    [queue]
  );

  const pulseEventsForDisplay = useMemo(() => {
    return [...asArray(activityLog), ...asArray(previewPulseEvents)]
      .filter((event) => event?.at || event?.created_at)
      .sort((a, b) => new Date(b.at || b.created_at).getTime() - new Date(a.at || a.created_at).getTime());
  }, [activityLog, previewPulseEvents]);

  const addPreviewPulseEvents = useCallback(() => {
    setPreviewPulseEvents(makePreviewPulseEvents());
    shouldFollowChat.current = true;
    setChatPaused(false);
    setTimeout(() => {
      scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
    }, 0);
  }, []);

  const clearPreviewPulseEvents = useCallback(() => {
    setPreviewPulseEvents([]);
  }, []);

  const sendChat = async (event) => {
    event.preventDefault();
    if (!chatInput.trim()) return;
    try {
      const { data } = await api.post("/chat/send", { message: chatInput });
      const results = Array.isArray(data?.results) ? data.results : [];
      const failed = results.filter((result) => result.action === "failed");
      setChatInput("");
      loadChat();
      if (failed.length > 0) {
        toast.warning(failed.map((result) => `${platformLabel(result.provider)}: ${result.error || "send failed"}`).join("  "));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed. Check chat connection.");
    }
  };

  const selectViewer = useCallback(async (viewer) => {
    const twitchUserId = viewer?.twitch_user_id || viewer?.user_id || viewer?.requester_twitch_user_id;
    if (!twitchUserId) {
      setSelectedViewer(viewer || null);
      setViewerNote(null);
      setNoteDraft("");
      setNoteError("No Twitch user ID is available for this viewer yet.");
      return;
    }
    const identity = {
      twitch_user_id: twitchUserId,
      login: viewer.login || viewer.username || viewer.requester_username || "",
      display_name: viewer.preferred_name || viewer.display_name || viewer.requester_display_name || viewer.login || viewer.username || twitchUserId,
      preferred_name: viewer.preferred_name || viewer.requester_preferred_name || "",
      pronouns: viewer.pronouns || viewer.requester_pronouns || "",
    };
    setSelectedViewer(identity);
    setViewerNote(null);
    setNoteDraft("");
    setNoteError("");
    try {
      const { data } = await api.get(`/admin/viewer-notes/${encodeURIComponent(twitchUserId)}`);
      setViewerNote(data);
      setNoteDraft(data.note || "");
    } catch (err) {
      setNoteError(err.response?.data?.detail || "Could not load viewer notes.");
    }
  }, []);

  const saveViewerNote = async () => {
    const twitchUserId = selectedViewer?.twitch_user_id;
    if (!twitchUserId) return;
    setNoteBusy(true);
    setNoteError("");
    try {
      const { data } = await api.patch(`/admin/viewer-notes/${encodeURIComponent(twitchUserId)}`, { note: noteDraft });
      setViewerNote(data);
      setNoteDraft(data.note || "");
      toast.success("Viewer note saved.");
    } catch (err) {
      setNoteError(err.response?.data?.detail || "Could not save viewer note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const startButtonEdit = (button) => {
    setEditingButtonId(button.id);
    setButtonDraft({
      label: button.label || "",
      icon: button.icon || "Sparkles",
      accent_color: button.accent_color || "#0A84FF",
      enabled: button.enabled !== false,
      mode: button.mode || "fire_once",
      button_type: button.button_type || "custom",
      current_state: Boolean(button.current_state),
      actions: asArray(button.actions).map(actionFromApi),
      on_actions: asArray(button.on_actions).map(actionFromApi),
      off_actions: asArray(button.off_actions).map(actionFromApi),
    });
    setStreamDeskError("");
  };

  const cancelButtonEdit = () => {
    setEditingButtonId("");
    setButtonDraft(null);
    setStreamDeskError("");
  };

  const saveButtonDraft = async () => {
    if (!buttonDraft) return;
    setStreamDeskBusy("save");
    setStreamDeskError("");
    try {
      const payload = {
        label: buttonDraft.label,
        icon: buttonDraft.icon,
        accent_color: buttonDraft.accent_color,
        enabled: buttonDraft.enabled,
        mode: buttonDraft.mode,
        button_type: buttonDraft.button_type,
        current_state: buttonDraft.current_state,
        actions: prepareActionsPayload(buttonDraft.actions),
        on_actions: prepareActionsPayload(buttonDraft.on_actions),
        off_actions: prepareActionsPayload(buttonDraft.off_actions),
      };
      const { data } = editingButtonId === "new"
        ? await api.post("/admin/stream-desk/buttons", payload)
        : await api.patch(`/admin/stream-desk/buttons/${editingButtonId}`, payload);
      setStreamDeskButtons((current) => {
        const buttons = asArray(current);
        const exists = buttons.some((item) => item.id === data.id);
        return exists ? buttons.map((item) => (item.id === data.id ? data : item)) : [...buttons, data];
      });
      cancelButtonEdit();
      toast.success("Stream Desk button saved.");
    } catch (err) {
      setStreamDeskError(err.response?.data?.detail || err.message || "Could not save Stream Desk button.");
    } finally {
      setStreamDeskBusy("");
    }
  };

  const createButton = () => {
    setEditingButtonId("new");
    setButtonDraft(defaultButtonDraft);
    setStreamDeskError("");
  };

  const pressButton = async (button) => {
    setStreamDeskBusy(button.id);
    setStreamDeskError("");
    try {
      const localObsPress = streamDeskLocalObsPress(button);
      if (localObsPress) {
        if (localObsPress.error) {
          const result = {
            runtime: "browser_obs",
            executed: false,
            status: "failed",
            action_results: [],
            outputs: {},
            error_message: localObsPress.error,
          };
          const { data } = await api.post(`/admin/stream-desk/buttons/${button.id}/local-result`, {
            result_metadata: result,
          });
          setStreamDeskButtons((current) => asArray(current).map((item) => (item.id === data.button.id ? data.button : item)));
          throw new Error(localObsPress.error);
        }
        if (!obsConnected) {
          connectObs();
          const message = `OBS is not connected in this browser${obsUrl ? ` (${obsUrl})` : ""}. Open OBS Control, connect, then press BRB again.`;
          const result = {
            runtime: "browser_obs",
            executed: false,
            status: "failed",
            action_results: [],
            outputs: {},
            error_message: message,
          };
          const { data } = await api.post(`/admin/stream-desk/buttons/${button.id}/local-result`, {
            result_metadata: result,
          });
          setStreamDeskButtons((current) => asArray(current).map((item) => (item.id === data.button.id ? data.button : item)));
          throw new Error(message);
        }
        const result = await executeLocalObsActionChain(localObsPress.actions, obsRequest, button);
        const { data } = await api.post(`/admin/stream-desk/buttons/${button.id}/local-result`, {
          current_state: button.mode === "toggle" && result.executed ? localObsPress.nextState : undefined,
          result_metadata: result,
        });
        setStreamDeskButtons((current) => asArray(current).map((item) => (item.id === data.button.id ? data.button : item)));
        toast[result.executed ? "success" : "error"](`${button.label || "Button"} ${result.executed ? "ran" : "failed"}.`);
        return;
      }
      const { data } = await api.post(`/admin/stream-desk/buttons/${button.id}/press`);
      setStreamDeskButtons((current) => asArray(current).map((item) => (item.id === data.button.id ? data.button : item)));
      const status = data.result?.executed ? "ran" : "failed";
      toast[data.result?.executed ? "success" : "error"](`${button.label || "Button"} ${status}.`);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Could not run Stream Desk button.";
      setStreamDeskError(message);
      toast.error(message);
    } finally {
      setStreamDeskBusy("");
    }
  };

  const toggleInteractionsPaused = async () => {
    const nextPaused = !status?.interactions_paused;
    setStreamDeskBusy("pause");
    setStreamDeskError("");
    try {
      await api.patch("/settings", { interactions_paused: nextPaused });
      setStatus((current) => ({ ...(current || {}), interactions_paused: nextPaused }));
      toast.success(nextPaused ? "Interactions paused." : "Interactions resumed.");
      await loadLiveData();
    } catch (err) {
      setStreamDeskError(err.response?.data?.detail || "Could not update interaction pause.");
    } finally {
      setStreamDeskBusy("");
    }
  };

  return (
    <div className="space-y-5" data-testid="streamer-panel-page">
      <section className="surface p-5 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Live streamer panel</div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Stream Desk</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
            Chat, live controls, giveaway tools, queue checks, and overlay actions for while the stream is running.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <StatusBadge
            good={backendReachable}
            icon={backendReachable ? Wifi : WifiOff}
            label={backendReachable ? "Backend online" : "Backend offline"}
          />
          <StatusBadge good={!!me?.authenticated} icon={Radio} label={me?.display_name || me?.login || "Admin session"} />
          <StatusBadge good={!!status?.chat_connected} icon={MessageSquare} label={status?.chat_connected ? "Chat connected" : "Chat unknown"} />
          <StatusBadge good={!!status?.eventsub_connected} icon={Activity} label={eventSubStatusLabel(status?.eventsub_status, status?.eventsub_connected)} />
        </div>
      </section>

      {status?.interactions_paused && (
        <div className="rounded border border-amber-400/45 bg-amber-400/10 px-4 py-3 flex items-center gap-2 text-amber-100">
          <PauseCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">Interactions Paused</span>
        </div>
      )}

      <section className="grid grid-cols-1 2xl:grid-cols-[460px_1fr_340px] gap-5 items-start">
        <ChatRail
          messages={messages}
          input={chatInput}
          setInput={setChatInput}
          sendChat={sendChat}
          scroller={scroller}
          pulseEvents={pulseEventsForDisplay}
          followPaused={chatPaused}
          onScroll={handleChatScroll}
          jumpToLatest={jumpChatToLatest}
          refresh={loadChat}
          badgeMap={chatBadges}
          onSelectViewer={selectViewer}
          previewCount={previewPulseEvents.length}
          onPreviewEvents={addPreviewPulseEvents}
          onClearPreview={clearPreviewPulseEvents}
        />

        <div className="space-y-5 min-w-0">
          <section className="surface p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <div className="label-eyebrow">Virtual streamdeck</div>
                <h2 className="text-xl font-semibold mt-1">Live action pads</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={toggleInteractionsPaused}
                  disabled={streamDeskBusy === "pause"}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-xs uppercase tracking-widest disabled:opacity-50 ${
                    status?.interactions_paused
                      ? "border-amber-400/60 bg-amber-400/10 text-amber-100"
                      : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
                  }`}
                >
                  <Pause className="w-3 h-3" /> {status?.interactions_paused ? "Resume interactions" : "Pause interactions"}
                </button>
                <button
                  type="button"
                  onClick={createButton}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[hsl(var(--border))] text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            </div>
            {streamDeskError && <div className="mb-3 text-sm text-[hsl(var(--destructive))]">{streamDeskError}</div>}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {asArray(streamDeskButtons).map((button) => (
                <StreamDeckButton
                  key={button.id}
                  button={button}
                  busy={streamDeskBusy === button.id}
                  onPress={() => pressButton(button)}
                  onEdit={() => startButtonEdit(button)}
                />
              ))}
              {asArray(streamDeskButtons).length === 0 && (
                <div className="col-span-full text-sm text-[hsl(var(--muted-foreground))]">
                  No Stream Desk buttons loaded.
                </div>
              )}
            </div>
            {buttonDraft && (
              <StreamDeckEditor
                draft={buttonDraft}
                setDraft={setButtonDraft}
                busy={streamDeskBusy === "save"}
                onSave={saveButtonDraft}
                onCancel={cancelButtonEdit}
              />
            )}
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <LiveStat title="Pending requests" value={pendingCount} body="Needs streamer attention." icon={ListChecks} />
            <LiveStat title="Pending sounds" value={asArray(redemptions).length} body="Sound redemptions waiting." icon={Volume2} />
            <LiveStat title="Recent events" value={asArray(events).length} body="Latest platform events loaded." icon={Activity} />
          </section>
          <HypeMonitor hype={hype} />
        </div>

        <aside className="space-y-5">
          <ViewerNotesPanel
            selectedViewer={selectedViewer}
            viewerNote={viewerNote}
            noteDraft={noteDraft}
            setNoteDraft={setNoteDraft}
            noteBusy={noteBusy}
            noteError={noteError}
            onSave={saveViewerNote}
          />
          <QueueSnapshot queue={queue} onSelectViewer={selectViewer} />
          <EventsSnapshot events={pulseEventsForDisplay.length ? pulseEventsForDisplay : streamPulseEvents(events)} />
        </aside>
      </section>
    </div>
  );
}

function HypeMonitor({ hype }) {
  const thresholds = hype?.level_thresholds || [0, 25, 75, 150, 300, 500];
  const level = hype?.current_level || 0;
  const current = thresholds[level] || 0;
  const next = thresholds[Math.min(level + 1, 5)] || thresholds[5] || 1;
  const progress = Math.max(0, Math.min(100, (((hype?.current_score || 0) - current) / Math.max(1, next - current)) * 100));
  return (
    <section className="surface p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="label-eyebrow">Hype monitor</div>
          <h2 className="text-xl font-semibold mt-1">Level {level}: {hype?.current_level_label || "quiet"}</h2>
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{hype?.current_score ?? 0} points · next level at {next}</div>
        </div>
        <Activity className="w-6 h-6 text-[hsl(var(--primary))]" />
      </div>
      <div className="mt-4 h-3 rounded bg-[hsl(var(--surface-2))] overflow-hidden">
        <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${progress}%` }} />
      </div>
    </section>
  );
}

function combinedChatFeed(messages, pulseEvents) {
  const chatItems = asArray(messages).map((message, index) => ({
    ...message,
    feed_type: "chat",
    feed_key: `chat-${message.ts || index}-${message.user_id || message.login || index}`,
    feed_at: message.ts || message.created_at || message.at,
  }));
  const eventItems = asArray(pulseEvents).map((event, index) => ({
    ...event,
    feed_type: "event",
    feed_key: `event-${event.id || event.source_event_key || event.source_event_id || index}`,
    feed_at: event.at || event.created_at,
  }));
  return [...chatItems, ...eventItems]
    .filter((item) => item.feed_at)
    .sort((a, b) => new Date(a.feed_at).getTime() - new Date(b.feed_at).getTime());
}

function ChatEventRow({ event }) {
  const platform = chatMessagePlatform(event);
  const platformMeta = streamDeckPlatformMeta[platform] || null;
  const pulseMeta = streamPulseMeta(event);
  const Icon = pulseMeta.icon;
  const detail = streamPulseDetail(event);
  return (
    <div
      className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))]/55 px-2.5 py-2 leading-relaxed"
      style={{
        borderLeft: `4px solid ${platformMeta?.color || pulseMeta.color}`,
        boxShadow: `inset 0 0 0 1px ${pulseMeta.color}16`,
      }}
      title={platformMeta ? `${platformMeta.label} stream moment` : "Stream moment"}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[hsl(var(--border))] bg-black/35" style={{ color: pulseMeta.color }}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{formatPulseTime(event.feed_at || event.at)}</span>
            <span className="font-semibold uppercase tracking-wider" style={{ color: pulseMeta.color }}>{pulseMeta.label}</span>
            {event.preview_only ? <MiniBadge>Preview</MiniBadge> : null}
          </div>
          <div className="text-white break-words mt-1">{detail.title}</div>
          {detail.body ? <div className="text-[hsl(var(--muted-foreground))] break-words mt-0.5">{detail.body}</div> : null}
        </div>
      </div>
    </div>
  );
}

function ChatRail({ messages, pulseEvents, input, setInput, sendChat, scroller, followPaused, onScroll, jumpToLatest, refresh, badgeMap, onSelectViewer, previewCount, onPreviewEvents, onClearPreview }) {
  const feedItems = combinedChatFeed(messages, pulseEvents);
  return (
    <section className="surface h-[78vh] min-h-[620px] flex flex-col overflow-hidden sticky top-4">
      <div className="p-4 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow">Live chat</div>
          <h2 className="text-lg font-semibold mt-1 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[hsl(var(--primary))]" /> Chatbar
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPreviewEvents}
            className="p-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
            title="Preview stream moment rows"
          >
            <Sparkles className="w-4 h-4" />
          </button>
          {previewCount > 0 ? (
            <button
              type="button"
              onClick={onClearPreview}
              className="px-2 py-2 rounded border border-[hsl(var(--border))] text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white"
              title="Clear preview events"
            >
              Clear
            </button>
          ) : null}
          <button
            type="button"
            onClick={refresh}
            className="p-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
            title="Refresh chat"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={scroller} onScroll={onScroll} className="relative flex-1 overflow-y-auto px-4 py-3 font-mono text-xs space-y-2" data-testid="streamer-chatbar">
        {feedItems.length === 0 && <div className="text-[hsl(var(--muted-foreground))]">No chat messages or stream moments yet.</div>}
        {feedItems.map((item, index) => {
          if (item.feed_type === "event") {
            return <ChatEventRow key={item.feed_key || `${item.type}-${index}`} event={item} />;
          }
          const message = item;
          const platform = chatMessagePlatform(message);
          const platformMeta = streamDeckPlatformMeta[platform] || null;
          return (
          <button
            type="button"
            key={`${message.ts}-${index}`}
            onClick={() => onSelectViewer(message)}
            className="block w-full text-left leading-relaxed rounded px-1 py-1 hover:bg-[hsl(var(--primary))]/10 transition"
            style={{ borderLeft: platformMeta ? `4px solid ${platformMeta.color}` : "4px solid transparent" }}
            title={platformMeta ? `${platformMeta.label} chat message` : undefined}
          >
            <div className="flex items-center gap-2 min-w-0 pl-2">
              <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0">{new Date(message.ts).toLocaleTimeString()}</span>
              <span className="font-semibold truncate" style={{ color: message.profile_accent_color || message.color || "#C7A3FF" }}>{message.preferred_name || message.display_name}</span>
              {message.preferred_name && (
                <span className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">@{message.display_name || message.login}</span>
              )}
              {message.is_bot_echo && <MiniBadge>Bot</MiniBadge>}
              {message.pronouns && <MiniBadge>{message.pronouns}</MiniBadge>}
              <BadgeStrip badges={message.badges || []} badgeMap={badgeMap} fallback={{
                is_broadcaster: message.is_broadcaster,
                is_mod: message.is_mod,
                is_vip: message.is_vip,
                is_subscriber: message.is_subscriber,
                provider: message.source_platform || message.channel,
              }} />
            </div>
            <div className="text-white break-words pl-16">
              <MessageWithEmotes message={message.message || ""} emotes={message.emotes || []} />
            </div>
          </button>
          );
        })}
        {followPaused && (
          <button
            type="button"
            onClick={jumpToLatest}
            className="sticky bottom-2 left-full ml-auto block rounded border border-[hsl(var(--primary))]/50 bg-black/85 px-3 py-1.5 text-[10px] uppercase tracking-widest text-[hsl(var(--primary))] shadow-lg"
          >
            Jump to latest
          </button>
        )}
      </div>

      <form onSubmit={sendChat} className="border-t border-[hsl(var(--border))] p-3 flex gap-2">
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Type as bot..."
          className="min-w-0 flex-1 bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
        />
        <button type="submit" className="px-3 py-2 bg-[hsl(var(--primary))] text-white text-sm rounded flex items-center gap-1.5">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </section>
  );
}

function platformLabel(provider) {
  const value = String(provider || "").toLowerCase();
  if (value === "kick") return "Kick";
  if (value === "twitch") return "Twitch";
  return provider || "Chat";
}

const streamPulseIncludeTerms = [
  "channel.channel_points_custom_reward_redemption",
  "channel.points",
  "channel.subscribe",
  "channel.subscription",
  "channel.cheer",
  "channel.bits",
  "channel.raid",
  "channel.follow",
  "subscription",
  "subscribe",
  "gift",
  "raid",
  "cheer",
  "bits",
  "reward",
  "redemption",
  "follow",
];

const streamPulseExcludeTerms = [
  "chat.message",
  "message.sent",
  "hype.",
  "stream.online",
  "stream.offline",
  "livestream.status",
];

function isStreamPulseEvent(event) {
  const type = String(event?.type || event?.kind || "").toLowerCase();
  const label = String(event?.label || "").toLowerCase();
  const detail = String(event?.detail || "").toLowerCase();
  const source = String(event?.source || "").toLowerCase();
  const text = `${type} ${label} ${detail} ${source}`;
  if (!text.trim()) return false;
  if (streamPulseExcludeTerms.some((term) => text.includes(term))) return false;
  if (isIndividualGiftRecipientEvent(event)) return false;
  return streamPulseIncludeTerms.some((term) => text.includes(term));
}

function streamPulseEvents(events) {
  return asArray(events).filter(isStreamPulseEvent).slice(0, 12);
}

function streamPulseBaseType(event) {
  return String(event?.type || event?.kind || "")
    .toLowerCase()
    .replace(/^twitch\./, "")
    .replace(/^kick\./, "");
}

function isIndividualGiftRecipientEvent(event) {
  const baseType = streamPulseBaseType(event);
  const metadata = asObject(event?.metadata);
  if (!baseType.endsWith("channel.subscribe")) return false;
  return Boolean(metadata.is_gift || metadata.gifted || metadata.gift || metadata.gifter_user_id || metadata.gifter_user_name);
}

function streamPulseMeta(event) {
  const baseType = streamPulseBaseType(event);
  const label = String(event?.label || "").toLowerCase();
  if (baseType.includes("channel_points") || baseType.includes("reward") || baseType.includes("redemption")) {
    return { label: "Channel point redeem", color: "#B85CFF", icon: Sparkles };
  }
  if (baseType.includes("kicks.gifted")) {
    return { label: "Kick gift", color: "#53FC18", icon: Zap };
  }
  if (baseType.includes("subscription.gift") || baseType.includes("subscription.gifts") || label.includes("gift")) {
    return { label: "Gifted subs", color: "#D8B4FE", icon: Gift };
  }
  if (baseType.includes("subscription.message") || label.includes("resub") || label.includes("streak")) {
    return { label: "Sub streak", color: "#C084FC", icon: Star };
  }
  if (baseType.includes("subscribe") || baseType.includes("subscription")) {
    return { label: "Subscription", color: "#A855F7", icon: Trophy };
  }
  if (baseType.includes("cheer") || baseType.includes("bits") || baseType.includes("kicks.gifted")) {
    return { label: "Cheer", color: "#38BDF8", icon: Zap };
  }
  if (baseType.includes("raid") || baseType.includes("host")) {
    return { label: "Raid / host", color: "#F472B6", icon: Users };
  }
  if (baseType.includes("follow")) {
    return { label: "Follow", color: "#22C55E", icon: Heart };
  }
  return { label: event?.label || "Stream moment", color: "#C7A3FF", icon: Bell };
}

function firstMetadataValue(metadata, keys) {
  for (const key of keys) {
    const value = metadata?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
}

function streamPulseDetail(event) {
  const metadata = asObject(event?.metadata);
  const baseType = streamPulseBaseType(event);
  const actor = firstMetadataValue(metadata, [
    "user_name",
    "user_login",
    "follower_username",
    "follower_login",
    "sender_username",
    "sender_login",
    "gifter_user_name",
    "gifter_user_login",
    "from_broadcaster_user_name",
    "from_broadcaster_user_login",
  ]);
  const reward = asObject(metadata.reward);
  const rewardTitle = firstMetadataValue(metadata, ["reward_title"]) || String(reward.title || "").trim();
  const message = firstMetadataValue(metadata, ["user_input", "message", "message_text", "text"]);
  const amount = firstMetadataValue(metadata, ["bits", "gift_amount", "amount", "total", "gift_count"]);
  const viewers = firstMetadataValue(metadata, ["viewers", "viewer_count"]);
  const label = event?.label || streamPulseMeta(event).label;
  const fallbackDetail = String(event?.detail || "").trim();

  if (baseType.includes("channel_points") || rewardTitle) {
    return {
      title: actor ? `${actor} redeemed ${rewardTitle || "a reward"}` : rewardTitle || label,
      body: message || fallbackDetail,
    };
  }
  if (baseType.includes("subscription.gift") || baseType.includes("subscription.gifts")) {
    return {
      title: actor ? `${actor} gifted ${amount || "subs"}` : `${amount || "Gifted"} subs`,
      body: fallbackDetail,
    };
  }
  if (baseType.includes("subscription.message")) {
    return {
      title: actor ? `${actor} shared a sub streak` : label,
      body: message || fallbackDetail,
    };
  }
  if (baseType.includes("subscribe") || baseType.includes("subscription")) {
    return {
      title: actor ? `${actor} subscribed` : label,
      body: message || fallbackDetail,
    };
  }
  if (baseType.includes("cheer") || baseType.includes("bits") || baseType.includes("kicks.gifted")) {
    return {
      title: actor ? `${actor} cheered ${amount || ""}`.trim() : label,
      body: message || fallbackDetail,
    };
  }
  if (baseType.includes("raid") || baseType.includes("host")) {
    return {
      title: actor ? `${actor} raided${viewers ? ` with ${viewers} viewers` : ""}` : label,
      body: fallbackDetail,
    };
  }
  if (baseType.includes("follow")) {
    return {
      title: actor ? `${actor} followed` : label,
      body: fallbackDetail,
    };
  }
  return {
    title: fallbackDetail || label,
    body: event?.type || "",
  };
}

function formatPulseTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString();
}

function makePreviewPulseEvents() {
  const now = Date.now();
  const at = (offset) => new Date(now + offset).toISOString();
  return [
    {
      id: `preview-redeem-${now}`,
      platform: "twitch",
      type: "twitch.channel.channel_points_custom_reward_redemption.add",
      label: "Channel Point Redeem",
      at: at(100),
      metadata: {
        user_name: "CutteredSwxine",
        reward_title: "Hydrate",
        user_input: "Drink water, Parker.",
        status: "fulfilled",
      },
      preview_only: true,
    },
    {
      id: `preview-sub-${now}`,
      platform: "twitch",
      type: "twitch.channel.subscribe",
      label: "Subscription",
      at: at(200),
      metadata: {
        user_name: "Sery_Bot",
        tier: "1000",
      },
      preview_only: true,
    },
    {
      id: `preview-gift-${now}`,
      platform: "twitch",
      type: "twitch.channel.subscription.gift",
      label: "Gift Sub",
      at: at(300),
      metadata: {
        gifter_user_name: "CausePlayGames",
        total: 5,
      },
      preview_only: true,
    },
    {
      id: `preview-cheer-${now}`,
      platform: "twitch",
      type: "twitch.channel.cheer",
      label: "Cheer",
      at: at(400),
      metadata: {
        user_name: "GhostFerretGaming",
        bits: 250,
        message: "For the chaos fund.",
      },
      preview_only: true,
    },
    {
      id: `preview-kick-${now}`,
      platform: "kick",
      type: "kick.kicks.gifted",
      label: "Kick Gift",
      at: at(500),
      metadata: {
        user_name: "KickViewer",
        gift_amount: 10,
        message: "Kick side checking in.",
      },
      preview_only: true,
    },
    {
      id: `preview-streak-${now}`,
      platform: "twitch",
      type: "twitch.channel.subscription.message",
      label: "Resub",
      at: at(600),
      metadata: {
        user_name: "FearlessKenji",
        message: "Six months in the barracks.",
      },
      preview_only: true,
    },
    {
      id: `preview-raid-${now}`,
      platform: "twitch",
      type: "twitch.channel.raid",
      label: "Raid",
      at: at(700),
      metadata: {
        from_broadcaster_user_name: "KatiePeterPlays",
        viewers: 42,
      },
      preview_only: true,
    },
  ];
}

const streamDeckPlatformMeta = {
  shared: {
    label: "Shared chat",
    color: "#38BDF8",
    text: "#DDF4FF",
    background: "rgba(56, 189, 248, 0.12)",
  },
  twitch: {
    label: "Twitch",
    color: "#9146FF",
    text: "#E8D9FF",
    background: "rgba(145, 70, 255, 0.14)",
  },
  kick: {
    label: "Kick",
    color: "#53FC18",
    text: "#D8FFD1",
    background: "rgba(83, 252, 24, 0.12)",
  },
};

function chatMessagePlatform(message) {
  if (message?.is_shared_chat === true || message?.shared_chat === true) return "shared";
  const value = String(
    message?.platform
      || message?.provider
      || message?.source
      || message?.source_platform
      || message?.service
      || message?.channel_platform
      || message?.channel
      || ""
  ).toLowerCase();
  if (value.includes("kick")) return "kick";
  if (value.includes("twitch")) return "twitch";
  if (String(message?.user_id || "").startsWith("kick:") || String(message?.twitch_user_id || "").startsWith("kick:")) return "kick";
  if (/^\d+$/.test(String(message?.user_id || message?.twitch_user_id || ""))) return "twitch";
  return "";
}

function ViewerNotesPanel({ selectedViewer, viewerNote, noteDraft, setNoteDraft, noteBusy, noteError, onSave }) {
  const displayName = viewerNote?.display_name || selectedViewer?.display_name || "Select a viewer";
  const login = viewerNote?.login || selectedViewer?.login || "";
  const pronouns = viewerNote?.pronouns || selectedViewer?.pronouns || "";

  return (
    <section className="surface p-5" data-testid="viewer-notes-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow">Viewer notes</div>
          <h2 className="text-lg font-semibold mt-1">Remember this person</h2>
        </div>
      </div>
      {!selectedViewer ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">
          Click a chatter or queue requester to pull up notes.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded border border-[hsl(var(--border))] bg-black/35 p-3">
            <div className="font-semibold text-[#f3ead3]">{displayName}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {login ? `@${login}` : selectedViewer.twitch_user_id}
              {pronouns ? ` - ${pronouns}` : ""}
            </div>
          </div>
          {noteError && <div className="text-xs text-[hsl(var(--destructive))]">{noteError}</div>}
          <textarea
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            maxLength={2000}
            placeholder="Notes for stream memory. Example: preferred topics, inside jokes, things to avoid, important context."
            className="w-full min-h-36 px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">{noteDraft.length}/2000</span>
            <button
              type="button"
              onClick={onSave}
              disabled={noteBusy}
              className="px-3 py-2 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-50"
            >
              {noteBusy ? "Saving..." : "Save note"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function StatusBadge({ good, icon: Icon, label = "" } = {}) {
  return (
    <div className={`inline-flex items-center gap-2 rounded border px-3 py-2 ${
      good
        ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
        : "border-[hsl(var(--border))] bg-black/35 text-[hsl(var(--muted-foreground))]"
    }`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
  );
}

function StreamDeckButton({ button, busy, onPress, onEdit }) {
  const actionCount = button.mode === "toggle"
    ? `${button.on_actions?.length || 0}/${button.off_actions?.length || 0}`
    : `${button.actions?.length || 0}`;
  const lastResult = button.last_result_metadata || {};
  const lastError = lastResult.error_message
    || lastResult.action_results?.find((result) => result?.error_message)?.error_message
    || "";
  return (
    <div
      className={`min-h-36 rounded border bg-black/55 p-3 transition relative overflow-visible ${
        button.enabled ? "border-[hsl(var(--border))]" : "border-[hsl(var(--border))] opacity-55"
      }`}
      style={{ boxShadow: button.current_state ? `inset 0 0 0 2px ${button.accent_color || "#0A84FF"}` : "none" }}
    >
      <button
        type="button"
        onClick={onEdit}
        className="absolute right-2 top-2 p-1.5 rounded bg-black/50 border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white"
        title="Edit button"
      >
        <Edit3 className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        disabled={!button.enabled || busy}
        onClick={onPress}
        className="w-full min-h-28 text-left disabled:cursor-not-allowed flex flex-col justify-end"
      >
        <CurrencyIcon currency={{ icon: button.icon, color: button.accent_color || "#0A84FF" }} className="w-6 h-6 mb-3 shrink-0" />
        <div className="font-semibold text-[#f3ead3] pr-8 leading-tight break-words">{button.label}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-snug">
          {button.mode === "toggle" ? (button.current_state ? "On" : "Off") : "Fire once"} - {actionCount} action{actionCount === "1" ? "" : "s"}
        </div>
        {lastResult.status && (
          <div
            className={`text-[11px] mt-2 leading-snug ${lastResult.status === "success" ? "text-emerald-300" : "text-[hsl(var(--destructive))]"}`}
            title={lastError || `Last: ${lastResult.status}`}
          >
            {lastResult.status === "success" ? "Last: success" : (lastError || "Last: failed")}
          </div>
        )}
      </button>
    </div>
  );
}

const localObsActionAliases = {
  "obs.filter.enable": "obs_filter_enable",
  "obs.filter.disable": "obs_filter_disable",
  "obs.filter_enable": "obs_filter_enable",
  "obs.filter_disable": "obs_filter_disable",
  "obs.filter:enable": "obs_filter_enable",
  "obs.filter:disable": "obs_filter_disable",
  "delay.wait": "delay",
};
const localObsActionTypes = new Set([
  "obs_filter",
  "obs_filter_enable",
  "obs_filter_disable",
  "show_source",
  "hide_source",
  "toggle_source",
  "delay",
]);

function normalizeLocalObsActionType(actionType) {
  const rawType = String(actionType || "").trim().toLowerCase();
  return localObsActionAliases[rawType] || rawType;
}

function streamDeskLocalObsPress(button) {
  const actions = button.mode === "toggle"
    ? (button.current_state ? button.off_actions : button.on_actions)
    : button.actions;
  const enabledActions = asArray(actions).filter((action) => action.enabled !== false);
  const isObsFilterButton = String(button.button_type || "").startsWith("obs.filter");
  if (enabledActions.length === 0) {
    return isObsFilterButton ? { actions: [], nextState: button.current_state, error: "BRB has no enabled OBS filter actions configured." } : null;
  }
  const unsupported = enabledActions.find((action) => !localObsActionTypes.has(normalizeLocalObsActionType(action.action_type)));
  if (unsupported) {
    if (!isObsFilterButton) return null;
    return {
      actions: [],
      nextState: button.current_state,
      error: `BRB has an unsupported local OBS action type: ${unsupported.action_type || "unknown"}`,
    };
  }
  return {
    actions: asArray(actions),
    nextState: button.mode === "toggle" ? !button.current_state : button.current_state,
  };
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function executeLocalObsActionChain(actions, obsRequest, button) {
  const actionResults = [];
  for (const action of actions || []) {
    const startedAt = new Date().toISOString();
    const actionType = normalizeLocalObsActionType(action.action_type);
    const result = {
      action_id: action.id,
      action_type: actionType,
      status: "skipped",
      started_at: startedAt,
      completed_at: null,
      originating_request_id: `stream-desk:${button.id}`,
      metadata: { action_type: actionType, executed: false, runtime: "browser_obs" },
      error_message: null,
    };
    if (action.enabled === false) {
      result.message = "Action is disabled";
      result.completed_at = new Date().toISOString();
      actionResults.push(result);
      continue;
    }
    const delayMs = Math.max(0, Number(action.delay_ms || 0));
    if (delayMs > 0) await wait(delayMs);
    if (actionType === "delay") {
      const delaySeconds = Number((action.config || {}).delay_seconds || 0);
      if (!Number.isFinite(delaySeconds) || delaySeconds <= 0) {
        result.status = "failed";
        result.error_message = "delay action requires config.delay_seconds greater than 0";
        result.completed_at = new Date().toISOString();
        actionResults.push(result);
        break;
      }
      await wait(delaySeconds * 1000);
      result.status = "success";
      result.message = `Waited ${delaySeconds} seconds`;
      result.metadata = { action_type: actionType, delay_seconds: delaySeconds, executed: true, runtime: "browser_obs" };
      result.completed_at = new Date().toISOString();
      actionResults.push(result);
      continue;
    }
    const config = action.config || parseActionConfigText(action.config_text);
    if (["show_source", "hide_source", "toggle_source"].includes(actionType)) {
      const sceneName = String(config.scene_name || "").trim();
      const sourceName = String(config.source_name || "").trim();
      const missing = [];
      if (!sceneName) missing.push("scene_name");
      if (!sourceName) missing.push("source_name");
      if (missing.length > 0) {
        result.status = "failed";
        result.error_message = `${actionType} action requires config.${missing.join(", config.")}`;
        result.completed_at = new Date().toISOString();
        actionResults.push(result);
        break;
      }
      try {
        const item = await findObsSceneItem(obsRequest, sceneName, sourceName);
        const sceneItemId = item.sceneItemId;
        const itemSceneName = item.sceneName || sceneName;
        let visible = actionType === "show_source";
        let previousVisible = null;
        if (actionType === "toggle_source") {
          const current = await obsRequest("GetSceneItemEnabled", {
            sceneName: itemSceneName,
            sceneItemId,
          });
          if (!("sceneItemEnabled" in current)) throw new Error("OBS did not return source visibility state");
          previousVisible = Boolean(current.sceneItemEnabled);
          visible = !previousVisible;
        }
        await obsRequest("SetSceneItemEnabled", {
          sceneName: itemSceneName,
          sceneItemId,
          sceneItemEnabled: visible,
        });
        result.status = "success";
        result.message = `${actionType === "toggle_source" ? "Toggled" : visible ? "Showed" : "Hid"} OBS source ${sourceName}`;
        result.metadata = {
          action_type: actionType,
          scene_name: itemSceneName,
          requested_scene_name: sceneName,
          source_name: sourceName,
          visible,
          previous_visible: previousVisible,
          executed: true,
          runtime: "browser_obs",
        };
        result.completed_at = new Date().toISOString();
        actionResults.push(result);
        continue;
      } catch (err) {
        result.status = "failed";
        result.error_message = err.message || "OBS source visibility action failed";
        result.completed_at = new Date().toISOString();
        actionResults.push(result);
        break;
      }
    }
    const sourceName = String(config.source_name || "").trim();
    const filterName = String(config.filter_name || "").trim();
    const missing = [];
    if (!sourceName) missing.push("source_name");
    if (!filterName) missing.push("filter_name");
    if (actionType === "obs_filter" && typeof config.enabled !== "boolean") missing.push("enabled");
    if (missing.length > 0) {
      result.status = "failed";
      result.error_message = `${actionType} action requires config.${missing.join(", config.")}`;
      result.completed_at = new Date().toISOString();
      actionResults.push(result);
      break;
    }
    const enabled = actionType === "obs_filter" ? config.enabled : actionType === "obs_filter_enable";
    try {
      await obsRequest("SetSourceFilterEnabled", {
        sourceName,
        filterName,
        filterEnabled: enabled,
      });
      result.status = "success";
      result.message = `${enabled ? "Enabled" : "Disabled"} OBS filter ${filterName} on ${sourceName}`;
      result.metadata = {
        action_type: actionType,
        scene_name: config.scene_name || null,
        source_name: sourceName,
        filter_name: filterName,
        enabled,
        executed: true,
        runtime: "browser_obs",
      };
      result.completed_at = new Date().toISOString();
      actionResults.push(result);
    } catch (err) {
      result.status = "failed";
      result.error_message = err.message || "OBS filter action failed";
      result.completed_at = new Date().toISOString();
      actionResults.push(result);
      break;
    }
  }
  const failed = actionResults.find((result) => result.status === "failed");
  return {
    runtime: "browser_obs",
    executed: !failed,
    status: failed ? "failed" : "success",
    action_results: actionResults,
    outputs: {},
    error_message: failed?.error_message || null,
  };
}

async function findObsSceneItem(obsRequest, sceneName, sourceName) {
  try {
    const direct = await obsRequest("GetSceneItemId", { sceneName, sourceName });
    if (direct.sceneItemId !== undefined && direct.sceneItemId !== null) {
      return { sceneName, sceneItemId: direct.sceneItemId };
    }
  } catch (err) {
    // If the source is inside an OBS group, it is not a direct item of the parent scene.
  }

  const searched = new Set();
  const searchScene = async (ownerSceneName, depth = 0) => {
    if (!ownerSceneName || searched.has(ownerSceneName) || depth > 5) return null;
    searched.add(ownerSceneName);
    let data;
    try {
      data = await obsRequest("GetSceneItemList", { sceneName: ownerSceneName });
    } catch (err) {
      data = await obsRequest("GetGroupSceneItemList", { sceneName: ownerSceneName });
    }
    for (const item of data.sceneItems || []) {
      if (item.sourceName === sourceName && item.sceneItemId !== undefined && item.sceneItemId !== null) {
        return { sceneName: ownerSceneName, sceneItemId: item.sceneItemId };
      }
      if (item.isGroup || item.inputKind === "group") {
        const nested = await searchScene(item.sourceName, depth + 1);
        if (nested) return nested;
      }
    }
    return null;
  };

  const found = await searchScene(sceneName);
  if (found) return found;
  throw new Error(`OBS source "${sourceName}" was not found directly in scene "${sceneName}" or inside its groups.`);
}

function parseActionConfigText(configText) {
  try {
    const parsed = JSON.parse(configText || "{}");
    return parsed && !Array.isArray(parsed) && typeof parsed === "object" ? parsed : {};
  } catch (err) {
    return {};
  }
}

function StreamDeckEditor({ draft, setDraft, busy, onSave, onCancel }) {
  const selectedFunction = streamDeckFunctionOptions.find((option) => option.value === draft.button_type);
  return (
    <div className="mt-5 rounded border border-[hsl(var(--border))] bg-black/35 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="label-eyebrow">Button config</div>
          <h3 className="text-lg font-semibold mt-1">Edit Stream Desk Button</h3>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-2 rounded border border-[hsl(var(--border))] text-sm text-[hsl(var(--muted-foreground))] hover:text-white">
            Cancel
          </button>
          <button type="button" onClick={onSave} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <DeskField label="Label">
          <input value={draft.label} onChange={(event) => setDraft({ ...draft, label: event.target.value })} className={deskInputClass} />
        </DeskField>
        <IconPicker label="Icon" value={draft.icon} color={draft.accent_color} onChange={(icon) => setDraft({ ...draft, icon })} test="stream-desk-icon-picker" />
        <DeskField label="Color">
          <input type="color" value={draft.accent_color || "#0A84FF"} onChange={(event) => setDraft({ ...draft, accent_color: event.target.value })} className="w-full h-10 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]" />
        </DeskField>
        <DeskField label="Mode">
          <select value={draft.mode} onChange={(event) => setDraft({ ...draft, mode: event.target.value })} className={deskInputClass}>
            <option value="fire_once">Fire once</option>
            <option value="toggle">Toggle</option>
          </select>
        </DeskField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_160px] gap-3">
        <DeskField label="Button function" help={selectedFunction?.description || "Existing custom function. You can keep it or choose one of the supported button functions."}>
          <select value={selectedFunction ? draft.button_type : "__custom_existing"} onChange={(event) => setDraft(applyStreamDeckFunction(draft, event.target.value))} className={deskInputClass}>
            {!selectedFunction && <option value="__custom_existing">{draft.button_type || "Existing custom function"}</option>}
            {streamDeckFunctionOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </DeskField>
        <label className="flex items-end gap-2 text-sm pb-2">
          <input type="checkbox" checked={draft.enabled} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} className="w-4 h-4" />
          Enabled
        </label>
        {draft.mode === "toggle" && (
          <label className="flex items-end gap-2 text-sm pb-2">
            <input type="checkbox" checked={draft.current_state} onChange={(event) => setDraft({ ...draft, current_state: event.target.checked })} className="w-4 h-4" />
            Currently on
          </label>
        )}
      </div>

      {draft.mode === "toggle" ? (
        <div className="space-y-4">
          <div className="rounded border border-[hsl(var(--border))] bg-black/20 p-3">
            <div className="label-eyebrow mb-1">When turning on</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">These runtime actions run when the button changes from off to on.</p>
            <ActionEditor actions={draft.on_actions || []} onChange={(on_actions) => setDraft({ ...draft, on_actions })} />
          </div>
          <div className="rounded border border-[hsl(var(--border))] bg-black/20 p-3">
            <div className="label-eyebrow mb-1">When turning off</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">These runtime actions run when the button changes from on to off.</p>
            <ActionEditor actions={draft.off_actions || []} onChange={(off_actions) => setDraft({ ...draft, off_actions })} />
          </div>
        </div>
      ) : (
        <div className="rounded border border-[hsl(var(--border))] bg-black/20 p-3">
          <div className="label-eyebrow mb-1">Actions</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">These runtime actions run once each time you press the button.</p>
          <ActionEditor actions={draft.actions || []} onChange={(actions) => setDraft({ ...draft, actions })} />
        </div>
      )}
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        These actions run through OBS / Media Runtime. Use the action cards above to build media playback, OBS filters, delays, and future runtime steps.
      </p>
    </div>
  );
}

const deskInputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]";
function DeskField({ label = "", help = "", children } = {}) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-2">{label}</div>
      {children}
      {help && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{help}</div>}
    </label>
  );
}

function LiveStat({ title, value, body, icon: Icon }) {
  return (
    <div className="surface p-5">
      <Icon className="w-5 h-5 text-[hsl(var(--primary))] mb-3" />
      <div className="text-3xl font-semibold">{value}</div>
      <div className="font-medium mt-1">{title}</div>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{body}</div>
    </div>
  );
}

function QueueSnapshot({ queue, onSelectViewer }) {
  const requestList = asArray(queue);
  return (
    <section className="surface p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow">Queue watch</div>
          <h2 className="text-lg font-semibold mt-1">Recent requests</h2>
        </div>
        <Link to="/app/queue" className="text-xs text-[hsl(var(--primary))] hover:underline">Open</Link>
      </div>
      {requestList.length === 0 ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">No interaction requests in the queue.</div>
      ) : (
        <div className="space-y-3">
          {requestList.slice(0, 6).map((request) => (
            <button
              key={request.request_id || request.id}
              type="button"
              onClick={() => onSelectViewer(request)}
              className="block w-full text-left rounded border border-[hsl(var(--border))] bg-black/35 p-3 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition"
            >
              <div className="font-medium truncate">{request.interaction_name || request.name || "Interaction request"}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {request.status || "pending"} {request.requester_preferred_name || request.requester_display_name ? `- ${request.requester_preferred_name || request.requester_display_name}` : ""}
                {request.requester_pronouns ? ` (${request.requester_pronouns})` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function EventsSnapshot({ events }) {
  const eventList = asArray(events);
  return (
    <section className="surface p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow">Stream pulse</div>
          <h2 className="text-lg font-semibold mt-1">Important moments</h2>
        </div>
        <Link to="/app/events" className="text-xs text-[hsl(var(--primary))] hover:underline">Open</Link>
      </div>
      {eventList.length === 0 ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">Waiting for subs, raids, cheers, follows, or channel point redeems.</div>
      ) : (
        <div className="space-y-3">
          {eventList.slice(0, 8).map((event) => {
            const pulseMeta = streamPulseMeta(event);
            const Icon = pulseMeta.icon;
            return (
            <div key={event.id || event.source_event_key || `${event.type}-${event.at}`} className="text-sm rounded border border-[hsl(var(--border))] bg-black/25 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-black/40" style={{ color: pulseMeta.color }}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="text-white font-medium truncate">{event.label || prettyEvent(event)}</div>
                </div>
                {event.platform ? (
                  <span className="shrink-0 rounded border border-[hsl(var(--border))] px-2 py-0.5 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                    {platformLabel(event.platform)}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                {event.detail || event.type || ""}
              </div>
              {formatPulseTime(event.at) ? (
                <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono mt-1">{formatPulseTime(event.at)}</div>
              ) : null}
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function BadgeStrip({ badges, badgeMap, fallback }) {
  const fallbackProvider = String(fallback.provider || "").toLowerCase();
  const fallbackColor = fallbackProvider === "kick" ? "#53fc18" : "";
  const rendered = asArray(badges)
    .map((rawBadge, index) => {
      const badge = normalizeBadgeForRender(rawBadge, fallbackProvider);
      if (!badge) return null;
      const badgeProvider = String(badge.provider || fallbackProvider || "").toLowerCase();
      const isKickBadge = badgeProvider === "kick";
      const key = `${badge.set_id}/${badge.version_id}`;
      const meta = isKickBadge ? null : badgeMap?.[key];
      const imageUrl = badge.image_url_2x || badge.image_url_1x || badge.image_url_4x || badge.image_url || badge.url || meta?.image_url_2x || meta?.image_url_1x || meta?.image_url_4x;
      const title = badge.title || badge.label || badge.name || meta?.title || humanizeBadgeId(badge.set_id);
      if (!imageUrl) {
        if (!badge.provider && meta) return null;
        if (isKickBadge) {
          const visualBadge = kickVisualBadgeMeta(badge);
          if (visualBadge) {
            return <KickBadgeFallback key={`${key}-${index}`} badge={visualBadge} title={title} />;
          }
        }
        return (
          <FallbackBadge
            key={`${key}-${index}`}
            title={title}
            color={badge.color || (isKickBadge ? "#53fc18" : fallbackColor)}
          >
            {badgeInitials(title || badge.set_id)}
          </FallbackBadge>
        );
      }
      return (
        <img
          key={`${key}-${index}`}
          src={imageUrl}
          alt={title}
          title={title}
          className="w-4 h-4 object-contain shrink-0"
        />
      );
    })
    .filter(Boolean);

  if (rendered.length > 0) {
    return <span className="inline-flex items-center gap-1 shrink-0">{rendered}</span>;
  }

  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      {fallback.is_broadcaster && (fallbackProvider === "kick" ? <KickBadgeFallback badge={KICK_VISUAL_BADGES.broadcaster} title="Kick Broadcaster" /> : <FallbackBadge title="Broadcaster">B</FallbackBadge>)}
      {!fallback.is_broadcaster && fallback.is_mod && (fallbackProvider === "kick" ? <KickBadgeFallback badge={KICK_VISUAL_BADGES.moderator} title="Moderator" /> : <FallbackBadge title="Moderator">M</FallbackBadge>)}
      {fallback.is_vip && (fallbackProvider === "kick" ? <KickBadgeFallback badge={KICK_VISUAL_BADGES.vip} title="VIP" /> : <FallbackBadge title="VIP" color={fallbackColor}>V</FallbackBadge>)}
      {fallback.is_subscriber && (fallbackProvider === "kick" ? <KickBadgeFallback badge={KICK_VISUAL_BADGES.subscriber} title="Subscriber" /> : <FallbackBadge title="Subscriber" color={fallbackColor}>S</FallbackBadge>)}
    </span>
  );
}

function normalizeBadgeForRender(rawBadge, fallbackProvider) {
  if (typeof rawBadge === "string") {
    const id = rawBadge.trim().toLowerCase();
    if (!id) return null;
    return {
      set_id: id,
      version_id: "1",
      title: id.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
      provider: fallbackProvider,
    };
  }
  if (!rawBadge || typeof rawBadge !== "object") return null;
  const setId = String(rawBadge.set_id || rawBadge.type || rawBadge.slug || rawBadge.id || rawBadge.name || "").trim().toLowerCase();
  if (!setId) return null;
  return {
    ...rawBadge,
    set_id: setId,
    version_id: String(rawBadge.version_id || rawBadge.version || "1"),
    provider: rawBadge.provider || fallbackProvider,
  };
}

function humanizeBadgeId(value) {
  return String(value || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const KICK_VISUAL_BADGES = {
  broadcaster: { title: "Kick Broadcaster", short: "K", shape: "circle", bg: "#53fc18", fg: "#071104", border: "#baff9f", assets: ["broadcaster"] },
  creator: { title: "Kick Creator", short: "K", shape: "circle", bg: "#53fc18", fg: "#071104", border: "#baff9f", assets: ["broadcaster", "creator"] },
  verified: { title: "Verified Creator", short: "V", shape: "seal", bg: "#d59b38", fg: "#ffffff", border: "#ffe2a1", assets: ["verified"] },
  moderator: { title: "Moderator", short: "M", shape: "shield", bg: "#49a7ff", fg: "#06111d", border: "#b9ddff", assets: ["moderator"] },
  vip: { title: "VIP", short: "V", shape: "diamond", bg: "#69d9ff", fg: "#03121a", border: "#c9f5ff", assets: ["vip"] },
  og: { title: "OG", short: "OG", shape: "square", bg: "#ff4f79", fg: "#ffffff", border: "#ffc2d0", assets: ["og"] },
  founder: { title: "Founder", short: "F", shape: "hex", bg: "#ffb04a", fg: "#1c0e00", border: "#ffe0ac", assets: ["founder", "founding_viewer"] },
  subscriber: { title: "Subscriber", short: "S", shape: "ticket", bg: "#53fc18", fg: "#071104", border: "#baff9f", assets: ["subscriber", "sub"] },
  gifted_sub: { title: "Gift Subscriber", short: "G", shape: "ticket", bg: "#a6ff68", fg: "#071104", border: "#dcffc9", assets: ["gifted_sub", "gift_sub"] },
  staff: { title: "Kick Staff", short: "ST", shape: "square", bg: "#ffd45a", fg: "#191000", border: "#fff1b7", assets: ["staff"] },
  admin: { title: "Kick Admin", short: "AD", shape: "square", bg: "#ffd45a", fg: "#191000", border: "#fff1b7", assets: ["admin", "staff"] },
  bot: { title: "Bot", short: "BOT", shape: "pill", bg: "#a78bfa", fg: "#100a28", border: "#ddd1ff", assets: ["bot"] },
  artist: { title: "Artist", short: "A", shape: "circle", bg: "#ff8bd1", fg: "#230014", border: "#ffd0ec", assets: ["artist"] },
};

const KICK_BADGE_ALIASES = {
  channel_owner: "broadcaster",
  channelowner: "broadcaster",
  owner: "broadcaster",
  host: "broadcaster",
  verified_creator: "verified",
  verifiedcreator: "verified",
  mod: "moderator",
  sub: "subscriber",
  gift_sub: "gifted_sub",
  giftsub: "gifted_sub",
  founding_viewer: "founder",
  foundingviewer: "founder",
};

function kickVisualBadgeMeta(badge) {
  const id = String(badge.set_id || badge.type || badge.slug || badge.id || badge.name || "").trim().toLowerCase();
  if (!id) return null;
  const compact = id.replace(/[\s-]+/g, "_");
  const noSeparator = compact.replace(/_/g, "");
  const key = KICK_VISUAL_BADGES[compact] ? compact : KICK_BADGE_ALIASES[compact] || KICK_BADGE_ALIASES[noSeparator] || noSeparator;
  return KICK_VISUAL_BADGES[key] || null;
}

function kickBadgeAssetSources(badge) {
  return asArray(badge.assets).map((name) => `https://cdn.aerokick.app/KickBadges/${encodeURIComponent(name)}.png`);
}

function KickBadgeFallback({ badge, title }) {
  const [sourceIndex, setSourceIndex] = useState(0);
  const sources = kickBadgeAssetSources(badge);
  const source = sources[sourceIndex];
  if (!source) return <KickVisualBadge badge={badge} title={title} />;
  return (
    <img
      src={source}
      alt={title || badge.title}
      title={title || badge.title}
      onError={() => setSourceIndex((value) => value + 1)}
      className="w-4 h-4 object-contain shrink-0"
    />
  );
}

function KickVisualBadge({ badge, title }) {
  const style = {
    "--kick-badge-bg": badge.bg,
    "--kick-badge-fg": badge.fg,
    "--kick-badge-border": badge.border,
  };
  const shapeClass = {
    circle: "rounded-full",
    diamond: "rotate-45 rounded-[3px]",
    hex: "[clip-path:polygon(25%_4%,75%_4%,100%_50%,75%_96%,25%_96%,0_50%)]",
    pill: "rounded-full w-5",
    seal: "rounded-full",
    shield: "[clip-path:polygon(14%_6%,86%_6%,86%_58%,50%_96%,14%_58%)]",
    square: "rounded-[3px]",
    ticket: "rounded-[2px]",
  }[badge.shape] || "rounded-[3px]";
  const textClass = badge.shape === "diamond" ? "-rotate-45 text-[7px]" : badge.short.length > 1 ? "text-[6px]" : "text-[8px]";
  return (
    <span
      title={title || badge.title}
      style={style}
      className={`inline-flex items-center justify-center w-4 h-4 shrink-0 border border-[var(--kick-badge-border)] bg-[var(--kick-badge-bg)] text-[var(--kick-badge-fg)] font-black leading-none shadow-[0_0_0_1px_rgba(0,0,0,0.55)] ${shapeClass}`}
    >
      <span className={textClass}>{badge.short}</span>
    </span>
  );
}

function badgeInitials(value) {
  const clean = String(value || "").replace(/[_-]+/g, " ").trim();
  if (!clean) return "?";
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}

function FallbackBadge({ title, children, color }) {
  const style = color ? { borderColor: color, color } : undefined;
  return (
    <span
      title={title}
      style={style}
      className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-[hsl(var(--primary))]/25 text-[9px] font-bold text-[#f3ead3] border border-[hsl(var(--primary))]/40"
    >
      {children}
    </span>
  );
}

const KICK_EMOTE_TOKEN_RE = /\[\s*emote\s*:\s*([A-Za-z0-9_-]+)\s*:\s*([^\]\r\n]+?)\s*\]/gi;

function kickEmoteUrl(id) {
  return `https://files.kick.com/emotes/${encodeURIComponent(id)}/fullsize`;
}

function kickEmoteSources(id) {
  const safe = encodeURIComponent(id);
  return [
    `https://files.kick.com/emotes/${safe}/fullsize`,
    `https://files.kick.com/emotes/${safe}/fullsize.webp`,
    `https://files.kick.com/emotes/${safe}/fullsize.png`,
    `https://files.kick.com/emotes/${safe}/2x`,
    `https://files.kick.com/emotes/${safe}/1x`,
  ];
}

function emotesFromKickTokens(message) {
  const out = [];
  const normalizedMessage = String(message || "");
  [...normalizedMessage.matchAll(KICK_EMOTE_TOKEN_RE)].forEach((match) => {
    const [token, id, rawName] = match;
    const offset = match.index ?? 0;
    const name = String(rawName || "").trim();
    out.push({
      id,
      name,
      start: offset,
      end: offset + token.length - 1,
      url: kickEmoteUrl(id),
      provider: "kick",
    });
  });
  return out;
}

function mergedMessageEmotes(message, emotes) {
  const merged = asArray(emotes).filter(Boolean).map((emote) => ({ ...emote }));
  const occupied = new Set(merged.map((emote) => `${Number(emote.start)}-${Number(emote.end)}`));
  emotesFromKickTokens(message).forEach((emote) => {
    const key = `${emote.start}-${emote.end}`;
    if (!occupied.has(key)) {
      occupied.add(key);
      merged.push(emote);
    }
  });
  return merged;
}

function KickEmoteImage({ id, name, text, className = "inline-block h-6 max-w-8 object-contain align-middle mx-0.5" }) {
  const [sourceIndex, setSourceIndex] = useState(0);
  const sources = kickEmoteSources(id);
  const fallback = name || text || `[emote:${id}]`;
  if (!sources[sourceIndex]) return fallback;
  return (
    <img
      src={sources[sourceIndex]}
      alt={fallback}
      title={fallback}
      onError={() => setSourceIndex((value) => value + 1)}
      className={className}
    />
  );
}

function MessageWithEmotes({ message, emotes }) {
  const rawMessage = String(message || "");
  const emoteList = mergedMessageEmotes(message, emotes);
  if (!emoteList.length) return message;

  const parts = [];
  let cursor = 0;
  const sorted = [...emoteList].sort((a, b) => Number(a.start) - Number(b.start));
  sorted.forEach((emote, index) => {
    const start = Number(emote.start);
    const end = Number(emote.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < cursor || end < start) return;
    if (start > cursor) {
      parts.push(rawMessage.slice(cursor, start));
    }
    const text = rawMessage.slice(start, end + 1);
    const imageUrl = emote.url || emote.image_url || emote.image_url_1x || emote.image_url_2x || emote.image_url_4x;
    if (emote.provider === "kick") {
      parts.push(
        <KickEmoteImage
          key={`${emote.id}-${start}-${index}`}
          id={emote.id}
          name={emote.name}
          text={text}
        />
      );
      cursor = end + 1;
      return;
    }
    parts.push(
      <img
        key={`${emote.id}-${start}-${index}`}
        src={imageUrl || `https://static-cdn.jtvnw.net/emoticons/v2/${encodeURIComponent(emote.id)}/default/dark/1.0`}
        alt={emote.name || text}
        title={emote.name || text}
        className="inline-block h-6 max-w-8 object-contain align-middle mx-0.5"
      />
    );
    cursor = end + 1;
  });
  if (cursor < rawMessage.length) {
    parts.push(rawMessage.slice(cursor));
  }
  return parts;
}

function MiniBadge({ children }) {
  return <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">{children}</span>;
}

function prettyEvent(event) {
  const type = event.type || event.kind || "event";
  const user = event.user_name || event.viewer || event.display_name || event.login;
  return user ? `${type} - ${user}` : type;
}

function eventSubStatusLabel(status, connected) {
  if (status === "webhook_ready") return "EventSub webhook ready";
  if (status === "websocket_connected") return "EventSub socket connected";
  if (status === "webhook_incomplete") return "EventSub setup incomplete";
  if (status === "websocket_waiting") return "EventSub waiting";
  if (status === "disabled") return "EventSub off";
  return connected ? "EventSub ready" : "EventSub unknown";
}

